/* eslint-disable react/no-unescaped-entities */
/**
 * Shared FONBEX primitives used across the preview pages.
 * Explicit inline styles based on the imposed palette so nothing leaks into
 * the production Tailwind theme.
 */
import React from "react";
import { BRAND } from "@/config/brand";

const C = BRAND.colors;

export const Btn = ({ variant = "primary", size = "md", children,
                     testid, style, ...rest }) => {
  const base = {
    display: "inline-flex", alignItems: "center", gap: 8,
    fontWeight: 600, letterSpacing: "0.01em",
    borderRadius: 6, cursor: "pointer",
    transition: "background-color .15s, border-color .15s, color .15s",
    fontFamily: "'IBM Plex Sans', system-ui, sans-serif",
    outline: "none",
  };
  const sizes = {
    sm: { padding: "6px 12px", fontSize: 13 },
    md: { padding: "10px 18px", fontSize: 14 },
    lg: { padding: "14px 24px", fontSize: 15 },
  };
  const variants = {
    primary: { backgroundColor: C.primary, color: C.white, border: `1px solid ${C.primary}` },
    secondary: { backgroundColor: "transparent", color: C.primary, border: `1px solid ${C.primary}` },
    destructive: { backgroundColor: C.accent, color: C.white, border: `1px solid ${C.accent}` },
    ghost: { backgroundColor: "transparent", color: C.textSecondary, border: "1px solid transparent" },
  };
  return (
    <button
      data-testid={testid}
      style={{ ...base, ...sizes[size], ...variants[variant], ...(style || {}) }}
      {...rest}
    >
      {children}
    </button>
  );
};

export const Chip = ({ children, tone = "info", testid }) => {
  const tones = {
    info: { bg: C.lightBlue, fg: C.primary },
    success: { bg: "#E7F7EF", fg: C.success },
    warning: { bg: "#FEF3C7", fg: C.warning },
    danger: { bg: "#FDE8EA", fg: C.accent },
    neutral: { bg: C.surface, fg: C.textSecondary },
    night: { bg: C.night, fg: C.white },
  };
  const t = tones[tone] || tones.info;
  return (
    <span
      data-testid={testid}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        backgroundColor: t.bg, color: t.fg,
        padding: "3px 10px", borderRadius: 4,
        fontSize: 12, fontWeight: 600, letterSpacing: "0.02em",
      }}
    >
      {children}
    </span>
  );
};

export const Section = ({ eyebrow, title, subtitle, children,
                          background = C.white, id, testid }) => (
  <section
    id={id}
    data-testid={testid}
    style={{ backgroundColor: background, padding: "96px 0", borderTop: `1px solid ${C.border}` }}
  >
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: "0 24px" }}>
      {eyebrow && (
        <div
          style={{
            color: C.primary, fontSize: 12, letterSpacing: "0.2em",
            textTransform: "uppercase", fontWeight: 700, marginBottom: 12,
          }}
        >
          {eyebrow}
        </div>
      )}
      {title && (
        <h2
          style={{
            fontFamily: "'Cabinet Grotesk', 'Space Grotesk', system-ui, sans-serif",
            fontSize: "clamp(28px, 4vw, 44px)",
            fontWeight: 700, letterSpacing: "-0.02em",
            color: C.night, lineHeight: 1.1, marginBottom: 16, maxWidth: 780,
          }}
        >
          {title}
        </h2>
      )}
      {subtitle && (
        <p
          style={{
            color: C.textSecondary, fontSize: 17, lineHeight: 1.55,
            maxWidth: 640, marginBottom: 48,
          }}
        >
          {subtitle}
        </p>
      )}
      {children}
    </div>
  </section>
);

export const Card = ({ children, style, testid, hoverable = false }) => (
  <div
    data-testid={testid}
    style={{
      backgroundColor: C.white,
      border: `1px solid ${C.border}`,
      borderRadius: 8,
      padding: 24,
      boxShadow: "0 2px 8px -2px rgba(16, 42, 86, 0.06)",
      transition: hoverable ? "border-color .15s, box-shadow .15s, transform .15s" : "none",
      ...style,
    }}
    onMouseEnter={(e) => {
      if (!hoverable) return;
      e.currentTarget.style.borderColor = C.primary;
      e.currentTarget.style.boxShadow = "0 8px 24px -6px rgba(36, 84, 211, 0.15)";
      e.currentTarget.style.transform = "translateY(-2px)";
    }}
    onMouseLeave={(e) => {
      if (!hoverable) return;
      e.currentTarget.style.borderColor = C.border;
      e.currentTarget.style.boxShadow = "0 2px 8px -2px rgba(16, 42, 86, 0.06)";
      e.currentTarget.style.transform = "translateY(0)";
    }}
  >
    {children}
  </div>
);

export const H1 = ({ children, style }) => (
  <h1
    style={{
      fontFamily: "'Cabinet Grotesk', 'Space Grotesk', system-ui, sans-serif",
      fontSize: "clamp(38px, 6vw, 68px)",
      fontWeight: 700, letterSpacing: "-0.03em",
      color: C.night, lineHeight: 1.05, ...style,
    }}
  >
    {children}
  </h1>
);

export const Money = ({ value, positive, currency = "€" }) => {
  const num = Number.isFinite(value) ? value : 0;
  const sign = num < 0 ? "-" : positive ? "+" : "";
  return (
    <span style={{ fontVariantNumeric: "tabular-nums", fontFamily: "'IBM Plex Sans'" }}>
      {sign}
      {Math.abs(num).toLocaleString("fr-FR")} {currency}
    </span>
  );
};

export const C_TOKENS = C;
